import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Clock, Sparkles, CheckCircle2 } from "lucide-react";

export default function PendingPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect when approved
  useEffect(() => {
    if (user?.status === "approved") navigate("/board", { replace: true });
    if (user?.status === "rejected") {
      // keep them on pending with rejection message — handled below
    }
  }, [user, navigate]);

  // Poll /auth/me every 15s so approval is picked up automatically
  useEffect(() => {
    const id = setInterval(() => { refreshUser?.(); }, 15000);
    return () => clearInterval(id);
  }, [refreshUser]);

  const rejected = user?.status === "rejected";

  return (
    <div className="min-h-screen bg-black text-white antialiased relative overflow-hidden flex items-center justify-center px-4">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      <div className="relative max-w-md w-full text-center" data-testid="pending-container">
        <div className="relative">
          <div
            className="absolute -inset-px pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, #00e5ff40 0%, transparent 50%, #00e5ff20 100%)",
            }}
          />
          <div className="relative bg-[#0a0a0a] border border-white/10 p-8 sm:p-10">
            <div className={`w-16 h-16 mx-auto border flex items-center justify-center mb-6 ${rejected ? "bg-red-500/10 border-red-500/30" : "bg-[#00e5ff]/10 border-[#00e5ff]/30"}`}>
              {rejected ? (
                <Clock size={30} className="text-red-400" strokeWidth={1.5} />
              ) : (
                <Clock size={30} className="text-[#00e5ff]" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="w-6 h-px bg-[#00e5ff]/60" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">
                {rejected ? "Zugang abgelehnt" : "Warteschleife"}
              </span>
              <span className="w-6 h-px bg-[#00e5ff]/60" />
            </div>
            <h1
              className="font-outfit font-black text-3xl sm:text-4xl text-white tracking-tighter leading-tight mb-4"
              data-testid="pending-heading"
            >
              {rejected ? (
                <>Zugang<br /><span className="text-red-400">abgelehnt</span></>
              ) : (
                <>Freischaltung<br /><span className="text-[#00e5ff]">ausstehend</span></>
              )}
            </h1>
            <p className="text-base text-white/65 leading-relaxed mb-2">
              Hallo {user?.first_name || "dort"}
              {rejected ? ", dein Zugang wurde leider abgelehnt." : ", deine Registrierung wurde empfangen."}
            </p>
            <p className="text-sm text-white/45 leading-relaxed mb-8">
              {rejected
                ? "Bei Fragen wende dich bitte direkt an den Admin."
                : "Ein Admin prüft dein Konto und schaltet dich anschließend frei. Du erhältst eine E-Mail, sobald du loslegen kannst. Diese Seite prüft automatisch alle 15 Sekunden deinen Status."}
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {!rejected && (
                <button
                  onClick={() => refreshUser?.()}
                  data-testid="refresh-status-btn"
                  className="group/btn relative overflow-hidden bg-[#00e5ff] text-black hover:bg-[#00e5ff] px-5 h-11 font-outfit font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] flex items-center gap-2"
                >
                  <Sparkles size={14} />
                  Jetzt prüfen
                </button>
              )}
              <button
                onClick={logout}
                data-testid="pending-logout-btn"
                className="px-4 h-11 text-sm text-white/50 hover:text-white font-outfit font-medium tracking-wide border border-white/10 hover:border-white/25 transition-all"
              >
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
